import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, MapPin, Building2, Tag, Package, List, Map as MapIconLucide } from "lucide-react";
import { toast } from "sonner";
import DetalleMontaje from "@/components/DetalleMontaje";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import MapaRutas from "@/components/MapaRutas";
import { MaterialBadge, UbicacionChip, LeyendaMaterial } from "@/components/MaterialBadge";

export default function RutaCreate() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  // Iter 57 — pre-fill desde Cronograma (?espacio=X&fecha=Y)
  const prefillEspacio = searchParams.get("espacio") || "";
  // Sep-2026 — pre-fill desde Reparto Inteligente (state: linea_ids, montador, fecha, nombre)
  const location = useLocation();
  const prefillState = location.state || {};
  const prefillFecha = searchParams.get("fecha") || prefillState.fecha || today;

  const [disponibles, setDisponibles] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [users, setUsers] = useState([]);
  const [externos, setExternos] = useState([]);
  const [provinciasActivas, setProvinciasActivas] = useState([]);

  const [filtroProv, setFiltroProv] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [vistaTrabajos, setVistaTrabajos] = useState("lista"); // "lista" | "mapa"
  const [dateField, setDateField] = useState("any"); // "any" | "fecha_objetivo" | "fecha_programada" | "fecha_montaje_prevista"
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Filtro "material listo hace > X días" — para priorizar paradas que llevan tiempo esperando ruta (Fernando P3)
  const [minDiasMaterial, setMinDiasMaterial] = useState(""); // "" | "3" | "7" | "14" | "30"
  // Agrupar por fecha en el listado (UX para 100+ paradas)
  const [agruparFecha, setAgruparFecha] = useState(true);

  const [ruta, setRuta] = useState({
    nombre: prefillState.nombre || "",
    fecha: prefillFecha,
    provincia_principal: "",
    provincia_secundaria: "",
    vehiculo_id: "",
    vehiculo_propio: false,
    km_inicial: "",
    hora_salida: "",
    hora_regreso: "",
    observaciones: "",
    nocturna: false,
    paradas: [],
    montadores: prefillState.montador?.usuario_id ? [{ tipo: "interno", id: prefillState.montador.usuario_id, rol: "principal" }]
      : prefillState.montador?.externo_id ? [{ tipo: "externo", id: prefillState.montador.externo_id, rol: "principal" }] : [],
  });
  const [saving, setSaving] = useState(false);
  const prefillAplicadoRef = useRef(false); // ref (no estado): inmune al doble efecto de StrictMode
  // Aplicar linea_ids del state una vez cargadas las disponibles (misma fuente que el selector manual).
  // Integridad: dedupe de ids y de disponibles por id; REEMPLAZA las paradas (nunca acumula).
  useEffect(() => {
    const ids = [...new Set((prefillState.linea_ids || []).filter(Boolean))];
    if (prefillAplicadoRef.current || !ids.length || !disponibles.length) return;
    prefillAplicadoRef.current = true;
    const porId = new Map();
    disponibles.forEach((l) => { if (ids.includes(l.id) && !porId.has(l.id)) porId.set(l.id, l); });
    const sel = ids.map((id) => porId.get(id)).filter(Boolean);
    setRuta((r) => ({ ...r, paradas: sel.map((l, i) => ({ linea_id: l.id, orden: i + 1, hora_estimada: "", observaciones: "", estado: "pendiente", _meta: l })) }));
    if (sel.length < ids.length) toast.warning(`${ids.length - sel.length} línea(s) de la propuesta ya no están disponibles para rutar`);
    else toast.success(`${sel.length} paradas cargadas desde Reparto inteligente`);
  }, [disponibles]); // eslint-disable-line react-hooks/exhaustive-deps
  // Iter 117 · Ausencias aprobadas que solapan la fecha de la ruta.
  // Se recargan cada vez que cambia `ruta.fecha` para marcar montadores no disponibles.
  const [ausentesEnFecha, setAusentesEnFecha] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/lineas/disponibles-para-rutar"),
      api.get("/vehiculos"),
      api.get("/users"),
      api.get("/colaboradores"),
      api.get("/meta/provincias-activas"),
    ]).then(([d, v, u, c, pa]) => {
      setDisponibles(d.data);
      setVehiculos(v.data);
      setUsers(u.data.filter((x) => x.active !== false));
      setExternos(c.data.filter((x) => x.activo !== false));
      setProvinciasActivas(pa.data.provincias || []);
    });
  }, []);

  // Iter 117 · Cargar ausencias cuando cambia la fecha de la ruta
  useEffect(() => {
    if (!ruta.fecha) { setAusentesEnFecha([]); return; }
    let alive = true;
    api.get("/rutas/montadores-ausentes", { params: { fecha: ruta.fecha } })
      .then((r) => { if (alive) setAusentesEnFecha(r.data || []); })
      .catch(() => { if (alive) setAusentesEnFecha([]); });
    return () => { alive = false; };
  }, [ruta.fecha]);

  // Provincias disponibles en los trabajos para filtrado interno
  const provincias = useMemo(() => {
    const s = new Set();
    disponibles.forEach((l) => l.espacio?.provincia && s.add(l.espacio.provincia));
    return Array.from(s).sort();
  }, [disponibles]);

  // Si el usuario elige provincia principal y aún no ha tocado el filtro, sincroniza
  useEffect(() => {
    if (ruta.provincia_principal && !filtroProv) setFiltroProv(ruta.provincia_principal);
  }, [ruta.provincia_principal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: obtiene la fecha "principal" de una línea (la misma jerarquía que el filtro)
  const fechaPrincipalDe = (l) => {
    if (dateField === "any") return (l.fecha_montaje_prevista || l.fecha_programada || l.peticion?.fecha_objetivo || "").slice(0, 10);
    if (dateField === "fecha_objetivo") return (l.peticion?.fecha_objetivo || "").slice(0, 10);
    return (l[dateField] || "").slice(0, 10);
  };

  // Helper: días que el material lleva listo (ahora — material_listo_desde)
  const diasMaterialListo = (l) => {
    if (!l.material_listo_desde) return null;
    const d = new Date(l.material_listo_desde);
    if (isNaN(d.getTime())) return null;
    const ms = Date.now() - d.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };

  const filtradas = disponibles.filter((l) => {
    if (ruta.paradas.find((p) => p.linea_id === l.id)) return false;
    if (filtroProv && l.espacio?.provincia !== filtroProv) return false;
    // Iter 57 — pre-filtrado por espacio cuando vienes del Cronograma
    if (prefillEspacio && l.espacio?.id !== prefillEspacio) return false;
    if (busqueda) {
      const t = `${l.peticion?.codigo || ""} ${l.peticion?.nombre || ""} ${l.peticion?.marca || ""} ${l.cliente_nombre || ""} ${l.espacio?.nombre || ""} ${l.espacio?.ciudad || ""}`.toLowerCase();
      if (!t.includes(busqueda.toLowerCase())) return false;
    }
    // Filtro por fechas
    if (dateFrom || dateTo) {
      const f = fechaPrincipalDe(l);
      if (!f) return false;
      if (dateFrom && f < dateFrom) return false;
      if (dateTo && f > dateTo) return false;
    }
    // Filtro "material listo hace > X días"
    if (minDiasMaterial) {
      const dias = diasMaterialListo(l);
      if (dias === null) return false;          // sin fecha de material listo → fuera cuando se filtra
      if (dias < Number(minDiasMaterial)) return false;
    }
    return true;
  });

  // Agrupación visual por fecha — devuelve [{ fecha, label, items: [...] }]
  const filtradasPorFecha = useMemo(() => {
    if (!agruparFecha || vistaTrabajos !== "lista") return null;
    const grupos = new Map();
    filtradas.forEach((l) => {
      const f = fechaPrincipalDe(l) || "__sin_fecha__";
      if (!grupos.has(f)) grupos.set(f, []);
      grupos.get(f).push(l);
    });
    // Orden: sin fecha al final, resto ASC
    const sorted = Array.from(grupos.entries()).sort(([a], [b]) => {
      if (a === "__sin_fecha__") return 1;
      if (b === "__sin_fecha__") return -1;
      return a.localeCompare(b);
    });
    return sorted.map(([fecha, items]) => {
      let label = "Sin fecha";
      if (fecha !== "__sin_fecha__") {
        const d = new Date(fecha);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const diffDias = Math.round((d - today) / 86400000);
        const prefix = diffDias === 0 ? "📍 HOY · " :
                       diffDias === 1 ? "⏭️ MAÑANA · " :
                       diffDias === -1 ? "⏮️ AYER · " :
                       diffDias < 0 ? `⚠️ HACE ${Math.abs(diffDias)} DÍAS · ` :
                       diffDias <= 7 ? "📅 " :
                       "📆 ";
        label = prefix + d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
      }
      return { fecha, label, items, esVencida: fecha !== "__sin_fecha__" && fecha < new Date().toISOString().slice(0, 10) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtradas, agruparFecha, vistaTrabajos, dateField]);

  const aplicarPresetFecha = (v) => {
    setDatePreset(v);
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (v === "custom") return;
    if (v === "all") { setDateFrom(""); setDateTo(""); return; }
    if (v === "today") { const t = iso(today); setDateFrom(t); setDateTo(t); return; }
    if (v === "tomorrow") { const d = new Date(today); d.setDate(d.getDate() + 1); const t = iso(d); setDateFrom(t); setDateTo(t); return; }
    if (v === "this_week") {
      const day = today.getDay() || 7;
      const start = new Date(today); start.setDate(today.getDate() - day + 1);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      setDateFrom(iso(start)); setDateTo(iso(end)); return;
    }
    if (v === "next_week") {
      const day = today.getDay() || 7;
      const start = new Date(today); start.setDate(today.getDate() - day + 1 + 7);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      setDateFrom(iso(start)); setDateTo(iso(end)); return;
    }
    if (v === "next_7") {
      const start = iso(today);
      const e = new Date(today); e.setDate(today.getDate() + 7);
      setDateFrom(start); setDateTo(iso(e)); return;
    }
    if (v === "next_14") {
      const start = iso(today);
      const e = new Date(today); e.setDate(today.getDate() + 14);
      setDateFrom(start); setDateTo(iso(e)); return;
    }
    if (v === "overdue") {
      // Vencidas: fecha < hoy
      const y = new Date(today); y.setDate(today.getDate() - 1);
      setDateFrom(""); setDateTo(iso(y)); return;
    }
  };

  const agregarParada = (l) => {
    // Jul-2026: si la fecha prevista de montaje no encaja con la fecha de la
    // ruta (mismo día o 1-2 días siguientes), pedimos confirmación explícita.
    const diff = diffPrevistaRuta(l);
    if (diff !== null && (diff < 0 || diff > 2)) {
      setConfirmFecha({ linea: l, prevista: fechaPrevistaDe(l), diff });
      return;
    }
    addParadaDirecto(l);
  };

  const addParadaDirecto = (l) => {
    setRuta((r) => r.paradas.some((p) => p.linea_id === l.id) ? r : ({
      ...r,
      paradas: [...r.paradas, {
        linea_id: l.id,
        orden: r.paradas.length + 1,
        hora_estimada: "",
        observaciones: "",
        estado: "pendiente",
        _meta: l,
      }],
    }));
  };

  // Fecha prevista de montaje de una línea (misma jerarquía que las etiquetas de la tarjeta)
  const fechaPrevistaDe = (l) =>
    (l.fecha_montaje_prevista || l.fecha_programada || l.peticion?.fecha_objetivo || "").slice(0, 10);

  // Días entre la fecha prevista del trabajo y la fecha de la ruta (null si falta alguna)
  const diffPrevistaRuta = (l) => {
    const prevista = fechaPrevistaDe(l);
    if (!prevista || !ruta.fecha) return null;
    return Math.round((new Date(prevista) - new Date(ruta.fecha)) / 86400000);
  };

  const [confirmFecha, setConfirmFecha] = useState(null); // { linea, prevista, diff }

  // Iter 71 — parada manual (sin línea, para Express/marrones)
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualDraft, setManualDraft] = useState({
    titulo: "", direccion: "", ciudad: "", provincia: "",
    contacto: "", telefono: "", duracion_min: "", observaciones: "",
    // Iter 125 · Tarea logística interna sin reporte de trabajo ni facturación
    tarea_interna: false,
  });

  const agregarParadaManual = () => {
    const t = manualDraft.titulo.trim();
    if (!t) {
      toast.error("Indica al menos un título");
      return;
    }
    setRuta((r) => ({
      ...r,
      paradas: [...r.paradas, {
        linea_id: null,
        es_manual: true,
        orden: r.paradas.length + 1,
        manual_titulo: t,
        manual_direccion: manualDraft.direccion.trim() || null,
        manual_ciudad: manualDraft.ciudad.trim() || null,
        manual_provincia: manualDraft.provincia.trim() || null,
        manual_contacto: manualDraft.contacto.trim() || null,
        manual_telefono: manualDraft.telefono.trim() || null,
        manual_duracion_min: manualDraft.duracion_min ? parseInt(manualDraft.duracion_min, 10) : null,
        hora_estimada: "",
        observaciones: manualDraft.observaciones.trim() || null,
        tarea_interna: !!manualDraft.tarea_interna,
        estado: "pendiente",
        _meta: { _manual: true, nombre: t, tarea_interna: !!manualDraft.tarea_interna },
      }],
    }));
    setManualDraft({ titulo: "", direccion: "", ciudad: "", provincia: "", contacto: "", telefono: "", duracion_min: "", observaciones: "", tarea_interna: false });
    setShowManualDialog(false);
    toast.success("Parada manual añadida");
  };

  // Renderiza una tarjeta de línea disponible. Extraído porque ahora se usa en 2 modos
  // (agrupado y plano).
  const renderLineaCard = (l) => {
    const dias = diasMaterialListo(l);
    return (
      <button key={l.id} type="button" onClick={() => agregarParada(l)}
        data-testid={`add-linea-${l.id}`}
        className={`w-full text-left bg-zinc-950/60 border ${l.es_revisita ? "border-cyan-500/40 hover:border-cyan-400" : "border-zinc-800 hover:border-amber-500/50"} rounded-lg p-2.5 transition-colors group`}>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <span className="text-[10px] text-amber-400 font-mono">{l.peticion?.codigo}</span>
            <MaterialBadge disponibilidad={l.disponibilidad} testId={`material-badge-${l.id}`} />
            {l.peticion?.marca && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300"><Tag className="w-2.5 h-2.5 inline" /> {l.peticion.marca}</span>}
            {l.es_revisita && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-200 font-bold flex items-center gap-0.5"
                title={l.notas_revisita || ""}
                data-testid={`badge-revisita-${l.id}`}>
                🔁 REVISITA {l.tipo_revisita === "medida" ? "· medida" : l.tipo_revisita === "tiempo" ? "· tiempo" : ""}
              </span>
            )}
            {/* Badge "X días esperando" — visible si lleva ≥ 3 días */}
            {dias !== null && dias >= 3 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                dias >= 30 ? "bg-red-500/25 text-red-200 border border-red-500/40" :
                dias >= 14 ? "bg-orange-500/20 text-orange-200 border border-orange-500/30" :
                dias >= 7  ? "bg-amber-500/20 text-amber-200 border border-amber-500/30" :
                             "bg-zinc-700/40 text-zinc-300 border border-zinc-600/40"
              }`}
                title={`Material listo desde ${l.material_listo_desde} (${dias} días)`}
                data-testid={`dias-esperando-${l.id}`}>
                ⏳ {dias}d
              </span>
            )}
          </div>
          <Plus className="w-3.5 h-3.5 text-zinc-500 group-hover:text-amber-400" />
        </div>
        <div className="text-xs text-zinc-200 font-semibold truncate">{l.espacio?.nombre}</div>
        <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5">
          <MapPin className="w-2.5 h-2.5" /> {l.espacio?.provincia || "—"} · {l.espacio?.ciudad || "—"}
        </div>
        <div className="text-[10px] text-zinc-500 mt-0.5">
          <Building2 className="w-2.5 h-2.5 inline mr-0.5" /> {l.cliente_nombre} ·
          {l.solo_montaje_sin_componentes ? (
            <span className="ml-1 text-amber-300 font-semibold" title="Solo montaje sin componentes producidos/recibidos">
              📦 Solo Montaje
            </span>
          ) : (
            <span className="ml-1">{l.num_componentes} comp{l.num_componentes !== 1 ? "s" : ""}</span>
          )}
        </div>
        {/* Sep-2026 · Detalle de montaje común (piezas diferenciadas por descripción, ref. cliente, zona) */}
        {l.detalle_montaje && (
          <div className="mt-1.5" data-testid={`linea-comps-preview-${l.id}`}>
            <DetalleMontaje detalle={l.detalle_montaje} campana={l.peticion?.codigo} marca={l.peticion?.marca} compacto max={4} testId={`detalle-montaje-${l.id}`} />
          </div>
        )}
        {/* Fechas relevantes */}
        {(l.fecha_montaje_prevista || l.fecha_programada || l.peticion?.fecha_objetivo) && (
          <div className="text-[10px] mt-1 flex flex-wrap items-center gap-1.5" data-testid={`linea-fechas-${l.id}`}>
            {l.fecha_montaje_prevista && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-mono"
                title="Cita confirmada por el cliente">
                📅 Cita: {new Date(l.fecha_montaje_prevista).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}
              </span>
            )}
            {l.fecha_programada && (
              <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30 font-mono"
                title="Fecha programada de la línea">
                ⏰ Prog: {new Date(l.fecha_programada).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}
              </span>
            )}
            {!l.fecha_montaje_prevista && !l.fecha_programada && l.peticion?.fecha_objetivo && (
              <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono"
                title="Fecha objetivo de la petición">
                🎯 Obj: {new Date(l.peticion.fecha_objetivo).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}
              </span>
            )}
            {(() => {
              const d = diffPrevistaRuta(l);
              if (d === null || (d >= 0 && d <= 2)) return null;
              return (
                <span
                  data-testid={`aviso-fecha-${l.id}`}
                  title={d > 2 ? "La fecha prevista es posterior a la fecha de esta ruta" : "La fecha prevista ya pasó respecto a la fecha de esta ruta"}
                  className={`px-1.5 py-0.5 rounded font-mono border ${d > 2
                    ? "bg-red-500/15 text-red-300 border-red-500/30"
                    : "bg-orange-500/15 text-orange-300 border-orange-500/30"}`}>
                  {d > 2 ? `⚠️ prevista en +${d}d` : `⏳ vencida hace ${-d}d`}
                </span>
              );
            })()}
          </div>
        )}
        {l.es_revisita && l.notas_revisita && (
          <div className="text-[10px] text-cyan-300 mt-1 italic line-clamp-2">
            💬 {l.notas_revisita}
          </div>
        )}
      </button>
    );
  };

  const quitarParada = (idx) => {
    setRuta((r) => {
      const arr = r.paradas.filter((_, i) => i !== idx).map((p, i) => ({ ...p, orden: i + 1 }));
      return { ...r, paradas: arr };
    });
  };

  const moverParada = (idx, dir) => {
    setRuta((r) => {
      const arr = [...r.paradas];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return r;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...r, paradas: arr.map((p, i) => ({ ...p, orden: i + 1 })) };
    });
  };

  const updateParada = (idx, patch) => {
    setRuta((r) => {
      const arr = [...r.paradas];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...r, paradas: arr };
    });
  };

  const addMontador = (tipo) => {
    setRuta((r) => ({ ...r, montadores: [...r.montadores, { tipo, id: "", rol: "principal" }] }));
  };

  const updateMontador = (idx, patch) => {
    setRuta((r) => {
      const arr = [...r.montadores];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...r, montadores: arr };
    });
  };

  const removeMontador = (idx) => {
    setRuta((r) => ({ ...r, montadores: r.montadores.filter((_, i) => i !== idx) }));
  };

  const guardar = async (esBorrador = false) => {
    if (!ruta.nombre || !ruta.fecha) {
      toast.error("Pon nombre y fecha");
      return;
    }
    if (ruta.paradas.length === 0 && !esBorrador) {
      if (!window.confirm("La ruta no tiene paradas asignadas. ¿Crear igualmente?")) return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: ruta.nombre,
        fecha: ruta.fecha,
        estado: esBorrador ? "borrador" : "planificada",
        provincia_principal: ruta.provincia_principal || null,
        provincia_secundaria: ruta.provincia_secundaria || null,
        vehiculo_id: ruta.vehiculo_propio ? null : (ruta.vehiculo_id || null),
        vehiculo_propio: !!ruta.vehiculo_propio,
        km_inicial: ruta.km_inicial ? parseFloat(ruta.km_inicial) : null,
        hora_salida: ruta.hora_salida || null,
        hora_regreso: ruta.hora_regreso || null,
        observaciones: ruta.observaciones || null,
        nocturna: !!ruta.nocturna,
        paradas: ruta.paradas.filter((p, i, arr) => !p.linea_id || arr.findIndex((x) => x.linea_id === p.linea_id) === i).map((p, i) => ({
          linea_id: p.linea_id || null,
          orden: i + 1,
          hora_estimada: p.hora_estimada || null,
          observaciones: p.observaciones || null,
          estado: "pendiente",
          // Iter 71 — campos manuales (solo para paradas sin petición)
          es_manual: !!p.es_manual,
          manual_titulo: p.manual_titulo || null,
          manual_direccion: p.manual_direccion || null,
          manual_ciudad: p.manual_ciudad || null,
          manual_provincia: p.manual_provincia || null,
          manual_contacto: p.manual_contacto || null,
          manual_telefono: p.manual_telefono || null,
          manual_duracion_min: p.manual_duracion_min || null,
          tarea_interna: !!p.tarea_interna,   // Iter 125
        })),
        montadores: ruta.montadores
          .filter((m) => m.id)
          .map((m) => {
            const obj = { rol: m.rol };
            if (m.tipo === "interno") {
              obj.usuario_id = m.id;
              const u = users.find((x) => x.id === m.id);
              obj.nombre_visible = u?.name || u?.email || "Montador";
            } else {
              obj.externo_id = m.id;
              const e = externos.find((x) => x.id === m.id);
              obj.nombre_visible = e?.nombre || "Externo";
            }
            return obj;
          }),
      };
      const { data } = await api.post("/rutas", payload);
      toast.success(esBorrador
        ? `Borrador ${data.codigo} guardado · nadie ha sido avisado`
        : `Ruta ${data.codigo} creada`);
      nav(`/rutas/${data.id}`);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? (det?.msg || "Error al crear") : (det || "Error al crear"), { duration: 9000 });
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-[1600px]">
      <PageHeader title="Nueva ruta" subtitle="Selecciona trabajos disponibles y arma el orden de paradas" />
      {prefillEspacio && (
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-sm text-cyan-200 flex items-center gap-2" data-testid="prefill-banner">
          <Package className="w-4 h-4" />
          <div className="flex-1">
            <b>Pre-rellenando ruta desde Cronograma</b> · solo verás los trabajos pendientes de este PDV.
            Marca los que quieras incluir y elige fecha/montadores.
          </div>
          <button onClick={() => nav("/rutas/nueva")} className="text-cyan-400 hover:text-cyan-300 text-xs underline">
            Quitar filtro
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-5">
        {/* COLUMNA IZQ — TRABAJOS DISPONIBLES */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-400" /> Trabajos disponibles
              <span className="text-xs text-zinc-500 font-normal">(material listo, sin ruta)</span>
            </div>
            <LeyendaMaterial className="hidden lg:flex" />
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-md bg-zinc-950 border border-zinc-800 p-0.5" data-testid="vista-toggle">
                <button type="button" onClick={() => setVistaTrabajos("lista")}
                  data-testid="vista-lista"
                  className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 transition-colors ${
                    vistaTrabajos === "lista" ? "bg-amber-500/20 text-amber-300" : "text-zinc-500 hover:text-zinc-300"
                  }`}>
                  <List className="w-3 h-3" /> Lista
                </button>
                <button type="button" onClick={() => setVistaTrabajos("mapa")}
                  data-testid="vista-mapa"
                  className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 transition-colors ${
                    vistaTrabajos === "mapa" ? "bg-amber-500/20 text-amber-300" : "text-zinc-500 hover:text-zinc-300"
                  }`}>
                  <MapIconLucide className="w-3 h-3" /> Mapa
                </button>
              </div>
              <span className="text-xs text-zinc-500" data-testid="filter-count">
                {filtradas.length}{disponibles.length !== filtradas.length ? <span className="text-amber-400"> /{disponibles.length}</span> : null}
              </span>
              {(filtroProv || busqueda || dateFrom || dateTo || minDiasMaterial) && (
                <button type="button"
                  onClick={() => {
                    setFiltroProv("");
                    setBusqueda("");
                    setDateFrom("");
                    setDateTo("");
                    setDatePreset("all");
                    setMinDiasMaterial("");
                  }}
                  data-testid="filter-clear-all"
                  className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30">
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <Select value={filtroProv || "todas"} onValueChange={(v) => setFiltroProv(v === "todas" ? "" : v)}>
              <SelectTrigger data-testid="filter-prov" className="bg-zinc-950 border-zinc-800 h-8 text-xs"><SelectValue placeholder="Provincia" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="todas">Todas las provincias</SelectItem>
                {provincias.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Buscar cliente, espacio, código…" value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              data-testid="filter-busqueda"
              className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
          </div>

          {/* Filtros de fecha — críticos cuando hay muchas líneas */}
          <div className="bg-zinc-950/40 border border-zinc-800/60 rounded p-2 mb-3">
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center justify-between">
              <span>📅 Filtro por fecha</span>
              {(dateFrom || dateTo) && (
                <button type="button"
                  onClick={() => { setDateFrom(""); setDateTo(""); setDatePreset("all"); }}
                  data-testid="filter-date-clear"
                  className="text-[9px] text-zinc-400 hover:text-amber-300">
                  Limpiar
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={dateField} onValueChange={setDateField}>
                <SelectTrigger data-testid="filter-date-field" className="bg-zinc-950 border-zinc-800 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="any">Cualquier fecha</SelectItem>
                  <SelectItem value="fecha_montaje_prevista">Cita confirmada (cliente)</SelectItem>
                  <SelectItem value="fecha_programada">Fecha programada (línea)</SelectItem>
                  <SelectItem value="fecha_objetivo">Fecha objetivo (petición)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={datePreset} onValueChange={aplicarPresetFecha}>
                <SelectTrigger data-testid="filter-date-preset" className="bg-zinc-950 border-zinc-800 h-8 text-xs">
                  <SelectValue placeholder="Rango" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="all">Todas las fechas</SelectItem>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="tomorrow">Mañana</SelectItem>
                  <SelectItem value="this_week">Esta semana</SelectItem>
                  <SelectItem value="next_week">Próxima semana</SelectItem>
                  <SelectItem value="next_7">Próximos 7 días</SelectItem>
                  <SelectItem value="next_14">Próximos 14 días</SelectItem>
                  <SelectItem value="overdue">⏰ Vencidas</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(datePreset === "custom" || dateFrom || dateTo) && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input type="date" value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setDatePreset("custom"); }}
                  data-testid="filter-date-from"
                  className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                <Input type="date" value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setDatePreset("custom"); }}
                  data-testid="filter-date-to"
                  className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
              </div>
            )}
          </div>

          {/* Filtro "Material listo desde" — prioriza paradas con material esperando */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-2 mt-2"
               data-testid="filter-material-listo">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-[11px] text-zinc-400 flex items-center gap-1">
                <Package className="w-3 h-3 text-amber-400" /> Material listo desde
              </Label>
              <Select value={minDiasMaterial || "all"} onValueChange={(v) => setMinDiasMaterial(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-material-dias" className="bg-zinc-950 border-zinc-800 h-7 w-44 text-xs">
                  <SelectValue placeholder="Sin filtro" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="all">Sin filtro</SelectItem>
                  <SelectItem value="3">≥ 3 días esperando</SelectItem>
                  <SelectItem value="7">≥ 1 semana esperando</SelectItem>
                  <SelectItem value="14">≥ 2 semanas esperando</SelectItem>
                  <SelectItem value="30">≥ 1 mes esperando</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Toggle agrupar por fecha (sólo en vista lista) */}
          {vistaTrabajos === "lista" && (
            <div className="flex items-center justify-end gap-2 mt-2 text-[11px] text-zinc-500">
              <label className="flex items-center gap-1.5 cursor-pointer hover:text-zinc-300"
                     data-testid="toggle-agrupar-fecha">
                <input type="checkbox" checked={agruparFecha}
                       onChange={(e) => setAgruparFecha(e.target.checked)}
                       className="w-3.5 h-3.5 rounded border-zinc-700 bg-zinc-900 accent-amber-500" />
                <span>📆 Agrupar por fecha</span>
              </label>
            </div>
          )}

          {vistaTrabajos === "mapa" ? (
            <MapaRutas
              disponibles={filtradas}
              paradas={ruta.paradas}
              onAddParada={agregarParada}
              onRemoveParada={quitarParada}
              height={560}
            />
          ) : (
            <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1" data-testid="lineas-disponibles">
              {filtradasPorFecha ? (
                /* Vista agrupada por fecha */
                filtradasPorFecha.map(({ fecha, label, items, esVencida }) => (
                  <div key={fecha} className="mt-2 first:mt-0" data-testid={`grupo-fecha-${fecha}`}>
                    <div className={`sticky top-0 z-10 px-2 py-1.5 mb-1.5 text-[10px] font-bold uppercase tracking-wider rounded backdrop-blur ${
                      esVencida ? "bg-red-500/20 text-red-200 border border-red-500/30" :
                      label.startsWith("📍") ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/30" :
                      label.startsWith("⏭️") ? "bg-amber-500/20 text-amber-200 border border-amber-500/30" :
                      "bg-zinc-800/80 text-zinc-300 border border-zinc-700/50"
                    }`}>
                      {label} <span className="ml-1 opacity-70">({items.length})</span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((l) => renderLineaCard(l))}
                    </div>
                  </div>
                ))
              ) : (
                /* Vista plana (sin agrupar) */
                filtradas.map((l) => renderLineaCard(l))
              )}
              {filtradas.length === 0 && (
                <div className="text-xs text-zinc-500 italic text-center py-6">Sin trabajos disponibles que coincidan.</div>
              )}
            </div>
          )}
        </div>

        {/* COLUMNA DER — RUTA */}
        <div className="space-y-4">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Nombre de la ruta *</Label>
                <Input value={ruta.nombre} onChange={(e) => setRuta({ ...ruta, nombre: e.target.value })}
                  data-testid="ruta-nombre"
                  placeholder="ej. Sevilla — Paco + Pablo · 18/05"
                  className="bg-zinc-950 border-zinc-800" />
              </div>
              <div><Label className="text-xs">Fecha *</Label>
                <Input type="date" value={ruta.fecha} onChange={(e) => setRuta({ ...ruta, fecha: e.target.value })}
                  data-testid="ruta-fecha" className="bg-zinc-950 border-zinc-800" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Salida</Label>
                  <Input type="time" value={ruta.hora_salida} onChange={(e) => setRuta({ ...ruta, hora_salida: e.target.value })}
                    className="bg-zinc-950 border-zinc-800" /></div>
                <div><Label className="text-xs">Regreso</Label>
                  <Input type="time" value={ruta.hora_regreso} onChange={(e) => setRuta({ ...ruta, hora_regreso: e.target.value })}
                    className="bg-zinc-950 border-zinc-800" /></div>
              </div>
              {/* Toggle Turno nocturno (ONCE, mantenimientos nocturnos, etc.) */}
              <label
                className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                  ruta.nocturna
                    ? "bg-indigo-500/10 border-indigo-500/40"
                    : "bg-zinc-950 border-zinc-800 hover:border-zinc-700"
                }`}
                data-testid="ruta-toggle-nocturna-label"
              >
                <input
                  type="checkbox"
                  checked={!!ruta.nocturna}
                  onChange={(e) => setRuta({ ...ruta, nocturna: e.target.checked })}
                  data-testid="ruta-toggle-nocturna"
                  className="mt-0.5 accent-indigo-500 w-4 h-4"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <span className="text-indigo-300">🌙 Turno nocturno</span>
                    {ruta.nocturna && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                        ACTIVO
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed">
                    Esta ruta se ejecuta total o parcialmente fuera de horario diurno (campañas ONCE, mantenimientos en estaciones de servicio, rotulación nocturna…). Activa esto para que los montadores asignados puedan <b>fichar la mañana siguiente</b> aunque la fecha de la ruta sea de "ayer". Las jornadas que cruzan medianoche quedan agrupadas en una sola.
                  </div>
                </div>
              </label>
              <div><Label className="text-xs">Provincia principal</Label>
                <Select value={ruta.provincia_principal || "ninguna"}
                  onValueChange={(v) => setRuta({ ...ruta, provincia_principal: v === "ninguna" ? "" : v })}>
                  <SelectTrigger data-testid="ruta-provincia-principal" className="bg-zinc-950 border-zinc-800">
                    <SelectValue placeholder="Selecciona provincia…" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
                    <SelectItem value="ninguna">— Sin definir —</SelectItem>
                    {provinciasActivas.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-zinc-500 italic">No hay provincias con trabajos activos</div>
                    )}
                    {provinciasActivas.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                {ruta.provincia_principal && (
                  <div className="text-[10px] text-amber-400/80 mt-0.5">Filtrando trabajos por {ruta.provincia_principal}</div>
                )}
              </div>
              <div><Label className="text-xs">Provincia 2ª (opcional)</Label>
                <Select value={ruta.provincia_secundaria || "ninguna"}
                  onValueChange={(v) => setRuta({ ...ruta, provincia_secundaria: v === "ninguna" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
                    <SelectItem value="ninguna">— Ninguna —</SelectItem>
                    {provinciasActivas.filter((p) => p !== ruta.provincia_principal).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Vehículo</Label>
                <Select value={ruta.vehiculo_propio ? "propio" : (ruta.vehiculo_id || "ninguno")}
                  onValueChange={(v) => {
                    if (v === "propio") setRuta({ ...ruta, vehiculo_propio: true, vehiculo_id: "" });
                    else setRuta({ ...ruta, vehiculo_propio: false, vehiculo_id: v === "ninguno" ? "" : v });
                  }}>
                  <SelectTrigger data-testid="ruta-vehiculo" className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="ninguno">Sin asignar</SelectItem>
                    <SelectItem value="propio">🚐 Vehículo propio del montador (autónomo)</SelectItem>
                    {vehiculos.filter((v) => v.activo !== false).map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.nombre} {v.matricula ? `(${v.matricula})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ruta.vehiculo_propio && (
                  <p className="text-[10px] text-amber-300 mt-1">No se exigirá parte de vehículo para esta ruta.</p>
                )}
              </div>
              <div><Label className="text-xs">Km salida</Label>
                <Input type="number" value={ruta.km_inicial} onChange={(e) => setRuta({ ...ruta, km_inicial: e.target.value })}
                  disabled={ruta.vehiculo_propio}
                  className="bg-zinc-950 border-zinc-800 disabled:opacity-40" /></div>
              <div className="col-span-2">
                <Label className="text-xs">Instrucciones generales</Label>
                <Textarea rows={2} value={ruta.observaciones} onChange={(e) => setRuta({ ...ruta, observaciones: e.target.value })}
                  placeholder="ej. Llaves del Decor Galva en oficina, llamar a Floren al llegar…"
                  className="bg-zinc-950 border-zinc-800" />
              </div>
            </div>
          </div>

          {/* Montadores */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold flex items-center gap-2">👷 Montadores</div>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => addMontador("interno")}
                  data-testid="add-montador-interno"
                  className="h-7 px-2 text-xs text-amber-300 hover:text-amber-200">
                  <Plus className="w-3 h-3 mr-1" /> DNF
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => addMontador("externo")}
                  data-testid="add-montador-externo"
                  className="h-7 px-2 text-xs text-violet-300 hover:text-violet-200">
                  <Plus className="w-3 h-3 mr-1" /> Externo
                </Button>
              </div>
            </div>
            {ruta.montadores.length === 0 && <div className="text-xs text-zinc-500 italic">Sin montadores. Añade al menos uno.</div>}
            {/* Iter 117 · Aviso si hay ausentes en la fecha seleccionada */}
            {ausentesEnFecha.length > 0 && ruta.fecha && (
              <div className="text-[11px] bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded px-2 py-1.5 mb-1.5"
                data-testid="aviso-ausentes-fecha">
                🌴 <b>{ausentesEnFecha.length} empleado(s) de baja/vacaciones el {ruta.fecha}:</b>{" "}
                {ausentesEnFecha.map((a) => `${a.user_nombre} (${a.tipo})`).join(" · ")}
              </div>
            )}
            <div className="space-y-1.5">
              {ruta.montadores.map((m, idx) => {
                const ausentesIds = new Set(ausentesEnFecha.map((a) => a.user_id));
                const lista = m.tipo === "interno"
                  ? users.filter((u) => (u.roles || []).includes("montador") || (u.roles || []).includes("admin"))
                  : externos;
                const estaAusente = m.tipo === "interno" && m.id && ausentesIds.has(m.id);
                const ausenciaInfo = estaAusente
                  ? ausentesEnFecha.find((a) => a.user_id === m.id)
                  : null;
                return (
                  <div key={idx} className="grid grid-cols-[80px_1fr_120px_auto] gap-2 items-center">
                    <span className={`text-[10px] uppercase tracking-wider text-center px-1.5 py-0.5 rounded ${m.tipo === "interno" ? "bg-amber-500/20 text-amber-300" : "bg-violet-500/20 text-violet-300"}`}>
                      {m.tipo === "interno" ? "DNF" : "Externo"}
                    </span>
                    <Select value={m.id || ""} onValueChange={(v) => updateMontador(idx, { id: v })}>
                      <SelectTrigger className={`bg-zinc-950 border-zinc-800 h-8 text-xs ${estaAusente ? "border-red-500/60 text-red-300" : ""}`}
                        data-testid={`montador-select-${idx}`}>
                        <SelectValue placeholder="Selecciona…" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        {lista.map((x) => {
                          const isAusente = m.tipo === "interno" && ausentesIds.has(x.id);
                          const info = isAusente ? ausentesEnFecha.find((a) => a.user_id === x.id) : null;
                          return (
                            <SelectItem key={x.id} value={x.id}
                              data-testid={`opt-montador-${x.id}`}>
                              {isAusente ? "🌴 " : ""}
                              {x.name || x.nombre || x.email}
                              {info ? ` — ${info.tipo}` : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Select value={m.rol} onValueChange={(v) => updateMontador(idx, { rol: v })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        <SelectItem value="principal">Principal</SelectItem>
                        <SelectItem value="ayudante">Ayudante</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeMontador(idx)}
                      className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
                    {estaAusente && ausenciaInfo && (
                      <div className="col-span-4 text-[10px] text-red-400 -mt-1"
                        data-testid={`montador-ausente-warn-${idx}`}>
                        ⚠️ {ausenciaInfo.user_nombre} está de {ausenciaInfo.tipo} del {ausenciaInfo.fecha_inicio} al {ausenciaInfo.fecha_fin}. El servidor bloqueará el guardado.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Paradas */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="text-sm font-semibold mb-2 flex items-center justify-between">
              <span>📍 Paradas en orden</span>
              <span className="text-xs text-zinc-500 font-normal">{ruta.paradas.length} parada{ruta.paradas.length !== 1 ? "s" : ""}</span>
            </div>
            {ruta.paradas.length === 0 && <div className="text-xs text-zinc-500 italic text-center py-4">Selecciona trabajos de la izquierda. Arrastra/usa las flechas para ordenar.</div>}
            <Button type="button" size="sm" variant="outline"
              onClick={() => setShowManualDialog(true)}
              data-testid="btn-add-parada-manual"
              className="w-full mb-2 border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Parada manual (sin petición)
            </Button>
            <div className="space-y-2" data-testid="paradas-list">
              {ruta.paradas.map((p, idx) => {
                const m = p._meta;
                const isManual = p.es_manual || m?._manual;
                const dir = isManual
                  ? [p.manual_direccion, p.manual_ciudad].filter(Boolean).join(", ")
                  : [m?.espacio?.direccion, m?.espacio?.cp, m?.espacio?.ciudad].filter(Boolean).join(", ");
                const mapsUrl = dir ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dir)}` : null;
                const titulo = isManual ? (p.manual_titulo || "Parada manual") : m?.espacio?.nombre;
                return (
                  <div key={idx} className={`bg-zinc-950/60 border rounded-lg p-2.5 ${isManual ? "border-amber-500/40" : "border-zinc-800"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-amber-500 text-zinc-900 font-bold text-xs w-6 h-6 rounded-full flex items-center justify-center">{idx + 1}</span>
                      {isManual ? (
                        p.tarea_interna ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 font-bold">🔧 INTERNA</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-bold">MANUAL</span>
                        )
                      ) : (
                        <div className="text-xs text-amber-400 font-mono">{m?.peticion?.codigo}</div>
                      )}
                      <div className="text-sm text-zinc-100 font-semibold truncate flex-1">{titulo}</div>
                      <div className="flex gap-0.5">
                        <Button type="button" size="sm" variant="ghost" disabled={idx === 0} onClick={() => moverParada(idx, -1)}
                          data-testid={`up-${idx}`} className="h-6 w-6 p-0 text-zinc-400 hover:text-amber-300 disabled:opacity-30"><ChevronUp className="w-3 h-3" /></Button>
                        <Button type="button" size="sm" variant="ghost" disabled={idx === ruta.paradas.length - 1} onClick={() => moverParada(idx, 1)}
                          data-testid={`down-${idx}`} className="h-6 w-6 p-0 text-zinc-400 hover:text-amber-300 disabled:opacity-30"><ChevronDown className="w-3 h-3" /></Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => quitarParada(idx)}
                          className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-400 flex items-center gap-2 mb-1.5">
                      <MapPin className="w-3 h-3" /> {dir || "Sin dirección"}
                      {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300 text-[10px]">Llévame ↗</a>}
                    </div>
                    {/* Iter 124 · Info detallada del montaje para distinguir paradas del mismo espacio */}
                    {!isManual && m?.componentes_resumen?.length > 0 && (
                      <div className="mb-1.5 bg-zinc-900/60 border border-zinc-800 rounded px-2 py-1 space-y-0.5"
                           data-testid={`parada-${idx}-comps`}>
                        {m.componentes_resumen.slice(0, 4).map((c) => (
                          <div key={c.id || c.nombre} className="text-[10px] text-zinc-300 flex items-start gap-1">
                            <UbicacionChip ubicacion={c.ubicacion} estado="" testId={`parada-${idx}-comp-ubicacion-${c.id}`} />
                            <span className="min-w-0 flex-1">
                              <b>{c.nombre}</b>
                              {c.cantidad > 1 && <span className="text-amber-300"> ×{c.cantidad}</span>}
                              {c.ancho_cm && c.alto_cm && <span className="text-zinc-500"> · {c.ancho_cm}×{c.alto_cm}cm</span>}
                              {c.material_nombre && <span className="text-zinc-500"> · {c.material_nombre}</span>}
                              {c.tipo_plantilla === "marco_vinilo" && <span className="text-violet-300"> · 📐</span>}
                              {c.descripcion_encargo && (
                                <span className="block text-[9px] text-amber-300/90 italic mt-0.5" title={c.descripcion_encargo}>
                                  ⚠️ {c.descripcion_encargo.length > 60 ? c.descripcion_encargo.slice(0, 60) + "…" : c.descripcion_encargo}
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                        {m.componentes_resumen.length > 4 && (
                          <div className="text-[9px] text-zinc-500 italic">+ {m.componentes_resumen.length - 4} más…</div>
                        )}
                      </div>
                    )}
                    {!isManual && m?.peticion?.descripcion && (
                      <div className="text-[10px] text-zinc-500 italic mb-1.5 line-clamp-2"
                           title={m.peticion.descripcion}
                           data-testid={`parada-${idx}-desc-peticion`}>
                        💬 {m.peticion.descripcion}
                      </div>
                    )}
                    <div className="grid grid-cols-[100px_1fr] gap-2">
                      <Input type="time" value={p.hora_estimada} onChange={(e) => updateParada(idx, { hora_estimada: e.target.value })}
                        placeholder="Hora"
                        className="bg-zinc-900 border-zinc-800 h-7 text-xs" />
                      <Input value={p.observaciones} onChange={(e) => updateParada(idx, { observaciones: e.target.value })}
                        placeholder="Instrucciones específicas para esta parada"
                        className="bg-zinc-900 border-zinc-800 h-7 text-xs" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => nav("/rutas")} className="text-zinc-400">Cancelar</Button>
            <Button onClick={() => guardar(true)} disabled={saving} data-testid="ruta-guardar-borrador"
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 font-semibold">
              📝 {saving ? "Guardando…" : "Guardar borrador"}
            </Button>
            <Button onClick={() => guardar(false)} disabled={saving} data-testid="ruta-guardar"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Crear ruta"}
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Diálogo · Parada manual (Iter 71) ─── */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent className="bg-zinc-950 border-amber-500/40 max-w-lg" data-testid="dialog-parada-manual">
          <DialogHeader>
            <DialogTitle className="text-amber-200">Añadir parada manual (sin petición)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-xs text-zinc-300">
              Para trabajos rápidos sin petición formal (Express, urgencias…). Esta parada no estará vinculada a ningún cliente del CRM.
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Título *</Label>
              <Input
                value={manualDraft.titulo}
                onChange={(e) => setManualDraft({ ...manualDraft, titulo: e.target.value })}
                placeholder="Ej: Reparación rótulo fachada"
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="manual-titulo"
                maxLength={120}
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Dirección</Label>
              <Input
                value={manualDraft.direccion}
                onChange={(e) => setManualDraft({ ...manualDraft, direccion: e.target.value })}
                placeholder="Calle, número"
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="manual-direccion"
                maxLength={200}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Ciudad</Label>
                <Input
                  value={manualDraft.ciudad}
                  onChange={(e) => setManualDraft({ ...manualDraft, ciudad: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="manual-ciudad"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Provincia</Label>
                <Input
                  value={manualDraft.provincia}
                  onChange={(e) => setManualDraft({ ...manualDraft, provincia: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="manual-provincia"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Contacto en sitio</Label>
                <Input
                  value={manualDraft.contacto}
                  onChange={(e) => setManualDraft({ ...manualDraft, contacto: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="manual-contacto"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Teléfono</Label>
                <Input
                  type="tel"
                  value={manualDraft.telefono}
                  onChange={(e) => setManualDraft({ ...manualDraft, telefono: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="manual-telefono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Duración estimada (min)</Label>
              <Input
                type="number"
                min="0"
                value={manualDraft.duracion_min}
                onChange={(e) => setManualDraft({ ...manualDraft, duracion_min: e.target.value })}
                placeholder="60"
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="manual-duracion"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Observaciones</Label>
              <Input
                value={manualDraft.observaciones}
                onChange={(e) => setManualDraft({ ...manualDraft, observaciones: e.target.value })}
                placeholder="Indicaciones específicas, herramientas, etc."
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="manual-obs"
                maxLength={500}
              />
            </div>
            {/* Iter 125 · Tarea logística interna sin reporte ni facturación */}
            <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-md border border-zinc-800 bg-zinc-900/40 hover:border-cyan-500/40 transition-colors"
              data-testid="manual-tarea-interna-label">
              <input
                type="checkbox"
                checked={manualDraft.tarea_interna}
                onChange={(e) => setManualDraft({ ...manualDraft, tarea_interna: e.target.checked })}
                className="mt-0.5"
                data-testid="manual-tarea-interna"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-cyan-200">⚙️ Tarea logística interna</div>
                <div className="text-[11px] text-zinc-400 leading-snug mt-0.5">
                  Ir a un proveedor a por materiales, dejar el vehículo en el taller… No pide reporte de trabajo,
                  ni albarán ni facturación. El montador solo marca "Hecho" (con foto opcional).
                </div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowManualDialog(false)} data-testid="manual-cancel">
              Cancelar
            </Button>
            <Button onClick={agregarParadaManual} disabled={!manualDraft.titulo.trim()}
              data-testid="manual-add"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold">
              Añadir parada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Jul-2026: confirmación cuando la fecha prevista no encaja con la fecha de la ruta */}
      <Dialog open={!!confirmFecha} onOpenChange={(o) => { if (!o) setConfirmFecha(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md" data-testid="confirm-fecha-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-300">⚠️ La fecha prevista no coincide</DialogTitle>
          </DialogHeader>
          {confirmFecha && (
            <div className="text-sm text-zinc-300 space-y-2">
              <p>
                <span className="font-semibold text-zinc-100">
                  {confirmFecha.linea.espacio?.nombre || confirmFecha.linea.descripcion || confirmFecha.linea.peticion?.nombre}
                </span>
                {confirmFecha.linea.peticion?.codigo && (
                  <span className="text-amber-400 font-mono text-xs ml-2">{confirmFecha.linea.peticion.codigo}</span>
                )}
              </p>
              <p>
                La fecha prevista de montaje es{" "}
                <span className="font-semibold text-amber-300">
                  {new Date(confirmFecha.prevista).toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" })}
                </span>
                {confirmFecha.diff > 0
                  ? <> — <span className="text-red-300 font-semibold">{confirmFecha.diff} día{confirmFecha.diff !== 1 ? "s" : ""} después</span> de la fecha de esta ruta ({new Date(ruta.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}).</>
                  : <> — ya <span className="text-orange-300 font-semibold">pasó hace {-confirmFecha.diff} día{confirmFecha.diff !== -1 ? "s" : ""}</span> respecto a la fecha de esta ruta ({new Date(ruta.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}).</>}
              </p>
              <p className="text-zinc-400">¿Seguro que quieres añadirla a esta ruta?</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmFecha(null)} data-testid="confirm-fecha-cancelar">
              Cancelar
            </Button>
            <Button
              onClick={() => { addParadaDirecto(confirmFecha.linea); setConfirmFecha(null); }}
              data-testid="confirm-fecha-aceptar"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold">
              Sí, añadir igualmente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
